import React, { useState, useEffect, useRef } from 'react';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Trophy } from 'lucide-react';
import { toast } from 'sonner';

const LEVEL_TITLES = {
  1: '🌱 Semilla consciente',
  2: '🌿 Brotecito constante',
  3: '🌾 Raíces fuertes',
  4: '🥑 Cocinillas vital',
  5: '💚 Aliado verde',
  6: '🕊️ Embajador/a vegano/a',
  7: '✨ Maestr@ del bienestar',
  8: '🌍 Leyenda Vital'
};

const LEVEL_THRESHOLDS = [0, 100, 300, 600, 1000, 1600, 2300, 3000];

interface UserLevelProps {
  compact?: boolean;
  showPoints?: boolean;
  points?: number;
  refreshKey?: number; // New prop to force refresh
}

const UserLevel: React.FC<UserLevelProps> = ({ 
  compact = false, 
  showPoints = true, 
  points: initialPoints,
  refreshKey = 0 // Default to 0
}) => {
  const { user } = useAuth();
  const [points, setPoints] = useState(initialPoints ?? 0);
  const [level, setLevel] = useState(1);
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(initialPoints === undefined);
  const lastPointsUpdateTime = useRef(0);
  const challengePointsQueue = useRef<number[]>([]);

  const calculateLevelWithTitle = (points: number) => {
    let calculatedLevel = 1;
    
    for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
      if (points >= LEVEL_THRESHOLDS[i]) {
        calculatedLevel = i + 1;
        break;
      }
    }
    
    calculatedLevel = Math.min(Math.max(calculatedLevel, 1), 8);
    
    const title = LEVEL_TITLES[calculatedLevel as keyof typeof LEVEL_TITLES] || LEVEL_TITLES[1];
    return { level: calculatedLevel, title };
  };

  const calculateProgress = (points: number, level: number) => {
    const currentLevelThreshold = LEVEL_THRESHOLDS[level - 1];
    
    const nextLevelThreshold = level < LEVEL_THRESHOLDS.length 
      ? LEVEL_THRESHOLDS[level] 
      : LEVEL_THRESHOLDS[level - 1] + 100;
    
    const pointsNeeded = nextLevelThreshold - currentLevelThreshold;
    
    const pointsInCurrentLevel = points - currentLevelThreshold;
    const progressPercentage = (pointsInCurrentLevel / pointsNeeded) * 100;
    
    return Math.min(Math.max(progressPercentage, 0), 100);
  };

  const showMedicalDisclaimer = (message: string) => {
    const medicalKeywords = [
      'enfermedad', 'embarazo', 'niño', 'patología', 'condición médica', 
      'enfermo', 'gestante', 'pediatría', 'tratamiento', 'diagnóstico'
    ];

    const lowercaseMessage = message.toLowerCase();
    const hasMedicalKeyword = medicalKeywords.some(keyword => 
      lowercaseMessage.includes(keyword)
    );

    if (hasMedicalKeyword) {
      toast.warning(
        'Lisa no sustituye un asesoramiento profesional. Puedes contactar con nuestra nutricionista directamente desde "Soporte 💚"', 
        { duration: 6000 }
      );
    }
  };

  // Process any queued challenge points
  const processQueuedPoints = async () => {
    if (challengePointsQueue.current.length === 0 || !user?.id) return;
    
    const now = Date.now();
    // Only process if at least 1 second has passed since last update
    if (now - lastPointsUpdateTime.current < 1000) return;
    
    try {
      // Sum all queued points
      const totalPointsToAdd = challengePointsQueue.current.reduce((sum, points) => sum + points, 0);
      console.log(`Processing queued points: ${totalPointsToAdd} from ${challengePointsQueue.current.length} challenges`);
      
      // Clear the queue
      challengePointsQueue.current = [];
      
      // Update the database with all accumulated points at once
      const { data: currentData, error: fetchError } = await supabase
        .from('profiles')
        .select('points')
        .eq('id', user.id)
        .single();
      
      if (fetchError) {
        console.error('Error fetching current points:', fetchError);
        return;
      }
      
      const currentPoints = currentData?.points || 0;
      const newPoints = currentPoints + totalPointsToAdd;
      
      console.log(`[UserLevel] Updating points for user ${user.id}: ${currentPoints} + ${totalPointsToAdd} = ${newPoints}`);
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ points: newPoints })
        .eq('id', user.id);
      
      if (updateError) {
        console.error('Error updating points:', updateError);
        return;
      }
      
      // Store updated points in localStorage for real-time UI updates
      localStorage.setItem(`user_points_${user.id}`, newPoints.toString());
      
      setPoints(newPoints);
      
      const newLevel = calculateLevelWithTitle(newPoints).level;
      setLevel(newLevel);
      
      const newProgress = calculateProgress(newPoints, newLevel);
      setProgress(newProgress);
      
      // Dispatch a points-updated event for other components
      const pointsUpdatedEvent = new CustomEvent('points-updated', {
        detail: { userId: user.id, newPoints: newPoints }
      });
      
      window.dispatchEvent(pointsUpdatedEvent);
      document.dispatchEvent(pointsUpdatedEvent);
      
      // Update the timestamp
      lastPointsUpdateTime.current = now;
      
      if (newLevel > calculateLevelWithTitle(currentPoints).level) {
        toast.success(`¡Subiste al nivel ${newLevel}! 🚀`, {
          description: "¡Felicidades por tu progreso!",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Error in processQueuedPoints:', error);
    }
  };

  useEffect(() => {
    // Force a fresh load of user points when refreshKey changes or component mounts
    if (user?.id) {
      console.log(`UserLevel refreshing data with refreshKey: ${refreshKey}`);
      loadUserPoints();
    }
  }, [user, refreshKey]);

  useEffect(() => {
    if (initialPoints !== undefined) {
      console.log("Setting initial points:", initialPoints);
      setPoints(initialPoints);
      const { level, title } = calculateLevelWithTitle(initialPoints);
      setLevel(level);
      const calculatedProgress = calculateProgress(initialPoints, level);
      setProgress(calculatedProgress);
    }
  }, [initialPoints]);

  useEffect(() => {
    const handleChallengeCompleted = (event: CustomEvent) => {
      console.log("Challenge completed event received in UserLevel:", event.detail);
      if (!user?.id) return;
      
      const pointsToAdd = event.detail?.points || 10;
      // Add points to queue instead of processing immediately
      challengePointsQueue.current.push(pointsToAdd);
      
      // Try to process the queue
      processQueuedPoints();
      
      toast.success(`¡Reto completado! 🎉`, {
        description: `Has ganado ${pointsToAdd} puntos por completar este reto.`,
        duration: 5000,
      });
    };

    const handlePointsUpdated = (event: CustomEvent) => {
      console.log("Points updated event received in UserLevel:", event.detail);
      if (user?.id === event.detail.userId) {
        const newPoints = event.detail.newPoints;
        console.log("Setting new points from event:", newPoints);
        setPoints(newPoints);
        
        const calculatedLevel = calculateLevelWithTitle(newPoints);
        setLevel(calculatedLevel.level);
        
        const calculatedProgress = calculateProgress(newPoints, calculatedLevel.level);
        setProgress(calculatedProgress);
      }
    };

    // Set up event listeners
    window.addEventListener('challenge-completed', handleChallengeCompleted as EventListener);
    window.addEventListener('points-updated', handlePointsUpdated as EventListener);

    // Set up timer to process queued points regularly
    const pointsTimer = setInterval(() => {
      if (challengePointsQueue.current.length > 0) {
        processQueuedPoints();
      }
    }, 1000);

    return () => {
      window.removeEventListener('challenge-completed', handleChallengeCompleted as EventListener);
      window.removeEventListener('points-updated', handlePointsUpdated as EventListener);
      clearInterval(pointsTimer);
    };
  }, [user]);

  const loadUserPoints = async () => {
    setIsLoading(true);
    try {
      console.log("Loading user points for user ID:", user?.id);
      const { data, error } = await supabase
        .from('profiles')
        .select('points')
        .eq('id', user?.id)
        .single();

      if (error) {
        console.error('Error loading user points:', error);
        return;
      }

      // Make sure we have a valid number for points, using 0 as fallback
      const userPoints = data?.points !== null && data?.points !== undefined ? Number(data.points) : 0;
      console.log("User points loaded from database:", userPoints);
      
      setPoints(userPoints);
      
      const calculatedLevel = calculateLevelWithTitle(userPoints);
      setLevel(calculatedLevel.level);
      
      const calculatedProgress = calculateProgress(userPoints, calculatedLevel.level);
      setProgress(calculatedProgress);
      
      if (user?.id) {
        localStorage.setItem(`user_points_${user.id}`, userPoints.toString());
      }
    } catch (error) {
      console.error('Error in loadUserPoints:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePoints = async (pointsToAdd: number) => {
    if (!user?.id) return;
    
    // Add points to queue instead of processing immediately
    challengePointsQueue.current.push(pointsToAdd);
    
    // Try to process the queue
    processQueuedPoints();

    showMedicalDisclaimer(pointsToAdd.toString());
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {!compact && (
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-yellow-500" />
            <span className="text-sm font-medium">
              Nivel {level}: {LEVEL_TITLES[level as keyof typeof LEVEL_TITLES] || LEVEL_TITLES[1]}
            </span>
          </div>
          {showPoints && (
            <span className="text-xs text-gray-500">{points} puntos</span>
          )}
        </div>
      )}
      <Progress value={progress} className="h-2" />
      {compact && showPoints && (
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-gray-600">
            Nivel {level}: {LEVEL_TITLES[level as keyof typeof LEVEL_TITLES] || LEVEL_TITLES[1]}
          </span>
          {showPoints && (
            <span className="text-xs text-gray-500">{points} pts</span>
          )}
        </div>
      )}
    </div>
  );
};

export default UserLevel;
